import React, { useEffect, createElement } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone } from 'lucide-react';
interface ServiceAreasProps {
  language: 'en' | 'es';
}
export function ServiceAreas({
  language
}: ServiceAreasProps) {
  const t = {
    en: {
      eyebrow: 'Where We Serve',
      title: 'SERVICE AREAS',
      primaryTitle: 'Primary Service Areas',
      surroundingTitle: 'Surrounding Areas',
      surroundingDesc: "We also serve surrounding communities within the Permian Basin. If you're nearby, chances are we can deliver to you.",
      notSure: 'Not sure if we serve your area?',
      callOrText: 'Call or Text Us'
    },
    es: {
      eyebrow: 'Dónde Servimos',
      title: 'ÁREAS DE SERVICIO',
      primaryTitle: 'Áreas de Servicio Principales',
      surroundingTitle: 'Áreas Circundantes',
      surroundingDesc: 'También servimos comunidades circundantes dentro de la Cuenca Permian. Si estás cerca, es probable que podamos entregarte.',
      notSure: '¿No estás seguro si servimos tu área?',
      callOrText: 'Llámanos o Escríbenos'
    }
  };
  const text = t[language];
  useEffect(() => {
    const initMap = () => {
      if (typeof window !== 'undefined' && window.L) {
        const mapElement = document.getElementById('map');
        if (mapElement && !mapElement.hasChildNodes()) {
          const map = window.L.map('map').setView([31.9973, -102.0779], 8);
          window.L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            subdomains: 'abcd',
            maxZoom: 20
          }).addTo(map);
          const cities = [{
            name: 'Odessa',
            coords: [31.8457, -102.3676]
          }, {
            name: 'Midland',
            coords: [31.9973, -102.0779]
          }, {
            name: 'Andrews',
            coords: [32.3187, -102.5457]
          }, {
            name: 'Pecos',
            coords: [31.4229, -103.4932]
          }, {
            name: 'Fort Stockton',
            coords: [30.894, -102.8793]
          }];
          cities.forEach(city => {
            const icon = window.L.divIcon({
              className: 'custom-div-icon',
              html: `<div style="background-color: #C9952A; width: 14px; height: 14px; border-radius: 50%; border: 2px solid #0A0A0A; box-shadow: 0 0 10px rgba(201, 149, 42, 0.8);"></div>`,
              iconSize: [14, 14],
              iconAnchor: [7, 7]
            });
            window.L.marker(city.coords, {
              icon
            }).addTo(map).bindPopup(`<b style="color: black; font-family: Inter;">${city.name}, TX</b>`);
          });
          const hqIcon = window.L.divIcon({
            className: 'custom-div-icon',
            html: `<div style="position: relative; display: flex; align-items: center; justify-content: center;">
              <svg width="36" height="44" viewBox="0 0 36 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 0C8.06 0 0 8.06 0 18c0 13.5 18 26 18 26s18-12.5 18-26C36 8.06 27.94 0 18 0z" fill="#C9952A"/>
                <circle cx="18" cy="18" r="8" fill="#0A0A0A"/>
                <circle cx="18" cy="18" r="4" fill="#C9952A"/>
              </svg>
            </div>`,
            iconSize: [36, 44],
            iconAnchor: [18, 44],
            popupAnchor: [0, -44]
          });
          window.L.marker([31.8457, -102.3676], {
            icon: hqIcon
          }).addTo(map).bindPopup(`<div style="font-family: Inter; text-align: center;">
                <b style="color: #C9952A; font-size: 14px;">Blackstone Rentals LLC</b><br/>
                <span style="color: #333; font-size: 12px;">Odessa, TX 79764</span><br/>
                <span style="color: #333; font-size: 12px;">📞 432-528-7892 / 432-803-4737</span>
              </div>`).openPopup();
        }
      }
    };
    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = initMap;
      document.head.appendChild(script);
    } else {
      initMap();
    }
  }, []);
  const primaryAreas = ['Odessa, TX', 'Midland, TX', 'Andrews, TX', 'Pecos, TX', 'Fort Stockton, TX', 'Monahans, TX', 'Kermit, TX', 'Crane, TX', 'Big Spring, TX', 'Stanton, TX'];
  return <section id="service-areas" className="py-12 md:py-20 bg-obsidian border-t border-steel w-full overflow-hidden" data-id="element-308">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-309">
        <div className="text-center mb-10 md:mb-16" data-id="element-310">
          <span className="font-montserrat text-gold text-xs md:text-sm font-bold tracking-widest uppercase mb-3 md:mb-4 block" data-id="element-311">
            {text.eyebrow}
          </span>
          <h2 className="font-oswald text-3xl sm:text-4xl md:text-5xl font-bold text-warmWhite uppercase" data-id="element-312">
            {text.title}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-313">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="h-[400px] md:h-[500px] rounded-lg overflow-hidden border-2 border-steel relative z-0" data-id="element-314">
            <div id="map" className="w-full h-full bg-carbon" data-id="element-315"></div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-carbon p-8 rounded-lg border border-steel" data-id="element-316">
            <h3 className="font-montserrat text-gold font-bold text-lg mb-6 tracking-wide flex items-center justify-center lg:justify-start gap-2" data-id="element-317">
              <MapPin size={18} color="#C9952A" data-id="element-318" /> {text.primaryTitle}
            </h3>

            <div className="grid grid-cols-2 gap-y-4 gap-x-4 mb-10" data-id="element-319">
              {primaryAreas.map((area, index) => <div key={index} className="flex items-center gap-3 font-inter text-warmWhite text-sm md:text-base" data-id="element-320">
                  <div className="w-2 h-2 rounded-full bg-gold flex-shrink-0" data-id="element-321"></div>
                  <span data-id="element-322">{area}</span>
                </div>)}
            </div>

            <div className="pt-8 border-t border-steel text-center lg:text-left" data-id="element-323">
              <h3 className="font-montserrat text-gold font-bold text-lg mb-3 tracking-wide flex items-center justify-center lg:justify-start gap-2" data-id="element-324">
                <MapPin size={18} color="#C9952A" data-id="element-325" /> {text.surroundingTitle}
              </h3>
              <p className="font-inter text-gray-300 mb-6 leading-relaxed" data-id="element-326">
                {text.surroundingDesc}
              </p>

              <div className="bg-obsidian p-5 rounded-lg border border-steel" data-id="element-327">
                <p className="font-inter text-warmWhite font-medium mb-1" data-id="element-328">
                  {text.notSure}
                </p>
                <p className="font-montserrat text-gold text-xs font-bold tracking-widest uppercase mb-3" data-id="element-329">
                  {text.callOrText}
                </p>
                <div className="flex flex-col gap-2" data-id="element-330">
                  <div className="flex items-center justify-center lg:justify-start gap-3" data-id="element-331">
                    <Phone size={18} color="#C9952A" className="flex-shrink-0" data-id="element-332" />
                    <span className="font-inter text-lg text-gold font-bold" data-id="element-333">
                      432-528-7892
                    </span>
                    <a href="tel:+14325287892" className="bg-gold/20 text-gold px-2 py-0.5 rounded text-xs font-bold hover:bg-gold hover:text-obsidian transition-all" data-id="element-334">
                      Call
                    </a>
                    <a href="sms:+14325287892" className="bg-gold/20 text-gold px-2 py-0.5 rounded text-xs font-bold hover:bg-gold hover:text-obsidian transition-all" data-id="element-335">
                      Text
                    </a>
                  </div>
                  <div className="flex items-center justify-center lg:justify-start gap-3" data-id="element-336">
                    <Phone size={18} color="#C9952A" className="flex-shrink-0" data-id="element-337" />
                    <span className="font-inter text-lg text-gold font-bold" data-id="element-338">
                      432-803-4737
                    </span>
                    <a href="tel:+14328034737" className="bg-gold/20 text-gold px-2 py-0.5 rounded text-xs font-bold hover:bg-gold hover:text-obsidian transition-all" data-id="element-339">
                      Call
                    </a>
                    <a href="sms:+14328034737" className="bg-gold/20 text-gold px-2 py-0.5 rounded text-xs font-bold hover:bg-gold hover:text-obsidian transition-all" data-id="element-340">
                      Text
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}